

import React, { useMemo } from 'react';
import {
  Droppable,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  DraggableProvidedDragHandleProps,
} from '@hello-pangea/dnd';
import { ContainerAPI } from '../../types/state.types';
import {
  DragIcon,
  ContainerIcon,
  ChevronIcon,
} from 'assests/GmsIcons';
import { DEEPEST_PARENT_LEVEL, sortByRowThenOrder } from './pageContainersHelpers';

// ============================================================================
// Row (private — only used by ContainerNode)
// ============================================================================

interface ContainerRowProps {
  container: ContainerAPI;
  level: number;
  isExpanded: boolean;
  toggleExpanded: (id: string) => void;
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
}

const ContainerRow = ({
  container,
  level,
  isExpanded,
  toggleExpanded,
  dragHandleProps,
}: ContainerRowProps) => {
  const nestedCount = container.content?.nestedContainers?.length || 0;
  const canHaveChildren = level <= DEEPEST_PARENT_LEVEL;
  const showChevron = canHaveChildren && nestedCount > 0;

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
      <div
        {...dragHandleProps}
        className="tw-cursor-grab tw-flex-shrink-0 tw-text-ink-tertiary icon-follow-ink"
        aria-label={`Drag ${container.name}`}
      >
        <DragIcon />
      </div>

      {showChevron ? (
        <button data-testid="configpanel-pagecontainernode-button-1"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded(container.id);
          }}
          className="tw-p-0 tw-border-none tw-bg-transparent tw-flex-shrink-0 tw-flex tw-items-center tw-text-ink-secondary icon-follow-ink"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <ChevronIcon isExpanded={isExpanded} />
        </button>
      ) : (
        <span className="tw-w-3 tw-flex-shrink-0" aria-hidden="true" />
      )}

      <span className="tw-text-ink-secondary icon-follow-ink" aria-hidden="true"><ContainerIcon /></span>
      <span
        className="tw-flex-1 tw-text-sm tw-text-ink tw-truncate"
        title={container.name}
      >
        {container.name || 'Container'}
      </span>
    </div>
  );
};

// ============================================================================
// Node (recursive — public export)
// ============================================================================

interface ContainerNodeProps {
  container: ContainerAPI;
  level: number;
  expandedIds: Set<string>;
  toggleExpanded: (id: string) => void;
  dragProvided: DraggableProvided;
  dragSnapshot: DraggableStateSnapshot;
}

export const ContainerNode = ({
  container,
  level,
  expandedIds,
  toggleExpanded,
  dragProvided,
  dragSnapshot,
}: ContainerNodeProps) => {
  const nestedContainers = useMemo(
    () => [...(container.content?.nestedContainers || [])].sort(sortByRowThenOrder),
    [container.content?.nestedContainers]
  );

  const canHaveChildren = level <= DEEPEST_PARENT_LEVEL;
  const hasChildren = canHaveChildren && nestedContainers.length > 0;
  const isExpanded = expandedIds.has(container.id);
  const childDroppableType = `container-L${level + 1}`;

  return (
    <div
      ref={dragProvided.innerRef}
      {...dragProvided.draggableProps}
      className={`tw-rounded tw-border tw-border-edge tw-bg-surface ${
        dragSnapshot.isDragging ? 'tw-shadow-md tw-bg-accent-tint' : 'hover:tw-bg-surface-hover'
      }`}
    >
      <ContainerRow
        container={container}
        level={level}
        isExpanded={isExpanded}
        toggleExpanded={toggleExpanded}
        dragHandleProps={dragProvided.dragHandleProps}
      />

      {hasChildren && isExpanded && (
        <Droppable droppableId={container.id} type={childDroppableType}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`tw-space-y-1 tw-rounded tw-mt-1 ${
                snapshot.isDraggingOver ? 'tw-bg-accent-tint' : ''
              }`}
              style={{ paddingLeft: `${(level + 1) * 16}px` }}
            >
              {nestedContainers.map((child, index) => (
                <Draggable key={child.id} draggableId={child.id} index={index}>
                  {(childDragProvided, childDragSnapshot) => (
                    <ContainerNode
                      container={child}
                      level={level + 1}
                      expandedIds={expandedIds}
                      toggleExpanded={toggleExpanded}
                      dragProvided={childDragProvided}
                      dragSnapshot={childDragSnapshot}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </div>
  );
};

export default ContainerNode;
